var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ConnectionError, InternalServerError, UnauthorizedError } from '../errors';
import { InvalidAudioError, InvalidContextMessageError, InvalidDocError, InvalidVideoError, MustBeAdminError, UserNotInChatError, } from './errors';
/**
 * Extracts the filename from a `Content-Disposition` header in the {@link Response}.
 * @param response `'attachment; filename="image.png"'` is an example value of the `Content-Disposition` header.
 * @returns {string} Example: `'image.png'`
 */
export function extractFilename(response) {
    const filename = response.headers.get('Content-Disposition').match(/filename=(.*)/)[1];
    /*
    The filename may be wrapped in double-quotes. We need to check if it starts and ends with a double quote instead of
    just checking if it contains a double quote because filenames may allow double quotes.
     */
    if (filename[0] === '"' && filename[filename.length - 1] === '"')
        return filename.substring(1, filename.length - 1);
    return filename;
}
/**
 * @param accessToken - You needn't pass an access token if the chat is public.
 * @param type - The type of media to read.
 * @param messageId - The message to read the media from.
 * @param imageType - Must be sent if retrieving a pic message.
 * @throws {@link UnauthorizedError}
 * @throws {@link ConnectionError}
 * @throws {@link InternalServerError}
 */
export function getMediaMessage({ apiUrl, protocol }, accessToken, type, messageId, imageType) {
    return __awaiter(this, void 0, void 0, function* () {
        const paramsInit = { 'message-id': messageId.toString() };
        if (imageType !== undefined)
            paramsInit['image-type'] = imageType;
        const params = new URLSearchParams(paramsInit).toString();
        const headers = {};
        if (accessToken !== undefined)
            headers.Authorization = `Bearer ${accessToken}`;
        const response = yield fetch(`${protocol}://${apiUrl}/${type}-message?${params}`, { headers });
        if (response.status >= 500 && response.status <= 599)
            throw new InternalServerError();
        switch (response.status) {
            case 200:
                return { filename: extractFilename(response), blob: yield response.blob() };
            case 401:
                throw new UnauthorizedError();
            default:
                throw new ConnectionError();
        }
    });
}
/**
 * Creates a media message. If the chat is a broadcast group, the user must be an admin.
 * @throws {@link InternalServerError}
 * @throws {@link UserNotInChatError}
 * @throws {@link InvalidAudioError}
 * @throws {@link InvalidVideoError}
 * @throws {@link InvalidDocError}
 * @throws {@link InvalidContextMessageError}
 * @throws {@link UnauthorizedError}
 * @throws {@link ConnectionError}
 */
export function postMediaMessage({ apiUrl, protocol }, accessToken, type, file, chatId, contextMessageId) {
    return __awaiter(this, void 0, void 0, function* () {
        const paramsInit = { 'chat-id': chatId.toString() };
        if (contextMessageId !== undefined)
            paramsInit['context-message-id'] = contextMessageId.toString();
        const params = new URLSearchParams(paramsInit).toString();
        const formData = new FormData();
        formData.append(type, file);
        const response = yield fetch(`${protocol}://${apiUrl}/${type}-message?${params}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
            body: formData,
        });
        if (response.status >= 500 && response.status <= 599)
            throw new InternalServerError();
        switch (response.status) {
            case 204:
                return;
            case 400:
                throw readInvalidMessageError(yield response.json(), type);
            case 401:
                throw new UnauthorizedError();
            default:
                throw new ConnectionError();
        }
    });
}
function readInvalidMessageError(message, type) {
    switch (message.reason) {
        case 'USER_NOT_IN_CHAT':
            return new UserNotInChatError();
        case 'INVALID_CONTEXT_MESSAGE':
            return new InvalidContextMessageError();
        case 'MUST_BE_ADMIN':
            return new MustBeAdminError();
        case 'INVALID_FILE':
            switch (type) {
                case 'audio':
                    return new InvalidAudioError();
                case 'video':
                    return new InvalidVideoError();
                case 'doc':
                    return new InvalidDocError();
            }
    }
}
